import React, { useState, useEffect } from 'react';
import {
  Wallet,
  Search,
  Plus,
  X,
  Edit2,
  Trash2,
  CheckCircle
} from 'lucide-react';
import api from '../../utils/api';
import './ExpensesPage.css';

const ExpensesPage = () => {
  // State for stats
  const [stats, setStats] = useState({
    totalExpenses: 0,
    pendingApproval: 0,
    approved: 0,
    paid: 0
  });

  // State for expenses
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    searchTitle: '',
    category: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    amount: '',
    date: '',
    description: '',
    vendor: '',
    receiptNo: '',
    paymentMethod: ''
  });

  // Constants
  const CATEGORIES = ['salary', 'utilities', 'maintenance', 'supplies', 'equipment', 'other'];
  const STATUSES = ['pending', 'approved', 'paid'];
  const STATUS_COLORS = {
    'pending': '#f59e0b',
    'approved': '#3b82f6',
    'paid': '#10b981'
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/expenses/stats');
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch expenses
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.searchTitle) params.append('title', filters.searchTitle);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await api.get(`/expenses?${params.toString()}`);
      const data = response.data.items || response.data;
      setExpenses(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchExpenses();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      amount: '',
      date: '',
      description: '',
      vendor: '',
      receiptNo: '',
      paymentMethod: ''
    });
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      if (selectedExpense) {
        // Edit mode
        await api.put(`/expenses/${selectedExpense.id}`, formData);
        setMessage('Expense updated successfully');
      } else {
        // Add mode
        await api.post('/expenses', formData);
        setMessage('Expense added successfully');
      }
      setShowAddModal(false);
      resetForm();
      setSelectedExpense(null);
      fetchExpenses();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error saving expense:', err);
      setError('Failed to save expense');
    }
  };

  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      title: expense.title,
      category: expense.category,
      amount: expense.amount,
      date: expense.date,
      description: expense.description || '',
      vendor: expense.vendor || '',
      receiptNo: expense.receiptNo || '',
      paymentMethod: expense.paymentMethod || ''
    });
    setShowAddModal(true);
  };

  const handleApproveExpense = async (expenseId) => {
    try {
      await api.put(`/expenses/${expenseId}/approve`, {});
      setMessage('Expense approved successfully');
      fetchExpenses();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error approving expense:', err);
      setError('Failed to approve expense');
    }
  };

  const handleDeleteExpense = async () => {
    try {
      await api.delete(`/expenses/${selectedExpense.id}`);
      setMessage('Expense deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedExpense(null);
      fetchExpenses();
      fetchStats();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError('Failed to delete expense');
    }
  };

  const getStatusBadgeStyle = (status) => {
    return {
      backgroundColor: STATUS_COLORS[status] || '#6b7280',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'capitalize'
    };
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowDeleteConfirm(false);
    if (!showAddModal) {
      setSelectedExpense(null);
      resetForm();
    }
  };

  return (
    <div className="expenses-page">
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card stat-total">
          <div className="stat-icon">
            <Wallet size={24} color="#b8860b" />
          </div>
          <div className="stat-content">
            <h3>Total Expenses</h3>
            <p className="stat-amount">{formatCurrency(stats.totalExpenses || 0)}</p>
          </div>
        </div>

        <div className="stat-card stat-pending">
          <div className="stat-icon">
            <Wallet size={24} color="#f59e0b" />
          </div>
          <div className="stat-content">
            <h3>Pending Approval</h3>
            <p className="stat-amount">{formatCurrency(stats.pendingApproval || 0)}</p>
          </div>
        </div>

        <div className="stat-card stat-approved">
          <div className="stat-icon">
            <Wallet size={24} color="#3b82f6" />
          </div>
          <div className="stat-content">
            <h3>Approved</h3>
            <p className="stat-amount">{formatCurrency(stats.approved || 0)}</p>
          </div>
        </div>

        <div className="stat-card stat-paid">
          <div className="stat-icon">
            <Wallet size={24} color="#10b981" />
          </div>
          <div className="stat-content">
            <h3>Paid</h3>
            <p className="stat-amount">{formatCurrency(stats.paid || 0)}</p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div className="filter-bar">
            <div className="filter-group">
              <div className="search-input-wrapper">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search expense title..."
                  name="searchTitle"
                  value={filters.searchTitle}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <select
                className="form-control"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                {STATUSES.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <input
                type="date"
                className="form-control"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                placeholder="From Date"
              />
            </div>

            <div className="filter-group">
              <input
                type="date"
                className="form-control"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                placeholder="To Date"
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setSelectedExpense(null);
                setShowAddModal(true);
              }}
            >
              <Plus size={18} /> Add Expense
            </button>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="card">
        <div className="card-header">
          <h2>Expenses</h2>
          <span className="record-count">{expenses.length} records</span>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="empty-state">
              <p>Loading expenses...</p>
            </div>
          ) : expenses.length === 0 ? (
            <div className="empty-state">
              <Wallet size={48} />
              <h3>No expenses found</h3>
              <p>No expenses match your current filters</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="expenses-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Receipt No</th>
                    <th>Payment Method</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense.id}>
                      <td className="expense-title">{expense.title}</td>
                      <td>
                        <span className="badge badge-secondary">
                          {expense.category.charAt(0).toUpperCase() + expense.category.slice(1)}
                        </span>
                      </td>
                      <td className="amount">{formatCurrency(expense.amount)}</td>
                      <td>{new Date(expense.date).toLocaleDateString('en-IN')}</td>
                      <td>{expense.vendor || '-'}</td>
                      <td className="receipt-no">{expense.receiptNo || '-'}</td>
                      <td className="payment-method">
                        {expense.paymentMethod ? (
                          <span className="text-capitalize">{expense.paymentMethod}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <span style={getStatusBadgeStyle(expense.status)}>
                          {expense.status}
                        </span>
                      </td>
                      <td className="actions">
                        {expense.status === 'pending' && (
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleApproveExpense(expense.id)}
                            title="Approve"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEditExpense(expense)}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setShowDeleteConfirm(true);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedExpense ? 'Edit Expense' : 'Add Expense'}</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddExpense}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="e.g., Monthly Electricity Bill"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      className="form-control"
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={formData.amount}
                      onChange={handleFormChange}
                      placeholder="0"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={formData.date}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows="3"
                    placeholder="Additional details about the expense..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Vendor/Supplier</label>
                    <input
                      type="text"
                      className="form-control"
                      name="vendor"
                      value={formData.vendor}
                      onChange={handleFormChange}
                      placeholder="Company or vendor name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Receipt No</label>
                    <input
                      type="text"
                      className="form-control"
                      name="receiptNo"
                      value={formData.receiptNo}
                      onChange={handleFormChange}
                      placeholder="Receipt or invoice number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Method</label>
                    <select
                      className="form-control"
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleFormChange}
                    >
                      <option value="">Select Method</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="bank transfer">Bank Transfer</option>
                      <option value="card">Card</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModals}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {selectedExpense ? 'Update Expense' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedExpense && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="close-btn" onClick={closeModals}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this expense?</p>
              <p className="text-muted"><strong>{selectedExpense.title}</strong> - {formatCurrency(selectedExpense.amount)}</p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModals}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteExpense}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
